import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramFumehoodFaceVelocity } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.fumehoodFaceVelocity.v1';

type Fields = { faceVelocity: string; faceArea: string; cfm: string };
const EMPTY: Fields = { faceVelocity: '', faceArea: '', cfm: '' };

/**
 * Fumehood Face Velocity — Q = V × A.
 */
const FumehoodFaceVelocityScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) {
      setError('Fill at least 2 of the 3 fields to solve for the missing one.');
      return;
    }
    if (filled === 3) {
      setError('All 3 values are filled — clear one to solve for it.');
      return;
    }

    const v = toImp(fields.faceVelocity, 'velocity');
    const a = toImp(fields.faceArea, 'area');
    const q = toImp(fields.cfm, 'flow');

    if (fields.cfm === '') {
      if (!Number.isFinite(v) || !Number.isFinite(a)) {
        setError('Need Face Velocity and Face Area to calculate Flow.');
        return;
      }
      setSolvedFor('cfm');
      set({ cfm: fromImp(v * a, 'flow', 2) });
    } else if (fields.faceVelocity === '') {
      if (!Number.isFinite(q) || !Number.isFinite(a) || a === 0) {
        setError('Face area cannot be zero.');
        return;
      }
      setSolvedFor('faceVelocity');
      set({ faceVelocity: fromImp(q / a, 'velocity', 2) });
    } else if (fields.faceArea === '') {
      if (!Number.isFinite(q) || !Number.isFinite(v) || v === 0) {
        setError('Face velocity cannot be zero.');
        return;
      }
      setSolvedFor('faceArea');
      set({ faceArea: fromImp(q / v, 'area', 2) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.faceVelocity && { label: 'Face Velocity', value: fields.faceVelocity, unit: unit('velocity'), emphasis: solvedFor === 'faceVelocity' },
    fields.faceArea && { label: 'Face Area', value: fields.faceArea, unit: unit('area'), emphasis: solvedFor === 'faceArea' },
    fields.cfm && { label: 'Flow', value: fields.cfm, unit: unit('flow'), emphasis: solvedFor === 'cfm' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Face Velocity & Flow"
      subtitle="Calculate face velocity, flow, or face area using Q = Face Velocity × Face Area"
      formula={
        <>
          <FormulaLine>Q = V × A</FormulaLine>
          <FormulaNote>
            Q = Flow ({unit('flow')}), V = Face Velocity ({unit('velocity')}), A = Face Area ({unit('area')})
          </FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: Q = V × A"
      diagram={<DiagramFumehoodFaceVelocity />}
      outputSlots={{
        velocity_fpm: fields.faceVelocity ? toImp(fields.faceVelocity, 'velocity') : undefined,
        area_sqft: fields.faceArea ? toImp(fields.faceArea, 'area') : undefined,
        cfm: fields.cfm ? toImp(fields.cfm, 'flow') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.velocity_fpm != null && !fields.faceVelocity) patch.faceVelocity = fromImp(values.velocity_fpm, 'velocity', 2);
        if (values.area_sqft != null && !fields.faceArea) patch.faceArea = fromImp(values.area_sqft, 'area', 3);
        if (values.cfm != null && !fields.cfm) patch.cfm = fromImp(values.cfm, 'flow', 2);
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Face Velocity (${unit('velocity')})`}
          value={fields.faceVelocity}
          onChangeText={(v) => set({ faceVelocity: v })}
          rightText={unit('velocity')}
          maxDecimals={2}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Face Area (${unit('area')})`}
          value={fields.faceArea}
          onChangeText={(v) => set({ faceArea: v })}
          rightText={unit('area')}
          maxDecimals={3}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Flow (${unit('flow')})`}
          value={fields.cfm}
          onChangeText={(v) => set({ cfm: v })}
          rightText={unit('flow')}
          maxDecimals={2}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default FumehoodFaceVelocityScreen;
